import { CSSProperties } from 'react';
import { CommentEntry, OverlayEntry } from './contentDiffComparisonTypes';

export const DIFF_MEASUREMENT_CONTAINER_STYLE: CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  top: '-9999px',
  width: '1080px',
  minHeight: '600px',
  overflow: 'visible',
  opacity: 0,
  pointerEvents: 'none',
  visibility: 'hidden',
};

export function waitForImages(container: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const images = container.querySelectorAll('img');
    if (images.length === 0) {
      resolve();
      return;
    }

    let loadedCount = 0;
    const totalImages = images.length;

    const checkComplete = () => {
      loadedCount++;
      if (loadedCount >= totalImages) {
        resolve();
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        checkComplete();
      } else {
        img.onload = checkComplete;
        img.onerror = checkComplete;
      }
    });
  });
}

export function computeOverlayPositions(
  container: HTMLElement,
  comments: CommentEntry[],
): OverlayEntry[] {
  const containerRect = container.getBoundingClientRect();

  return comments
    .map((comment) => {
      const sectionElement = container.querySelector<HTMLElement>(
        `[data-comment-ids*="${comment.conversationId}"]`,
      );

      if (!sectionElement) {
        return null;
      }

      const rect = sectionElement.getBoundingClientRect();

      return {
        comment,
        top: rect.top - containerRect.top + 10,
        right: containerRect.width - (rect.left - containerRect.left + rect.width) + 20,
      };
    })
    .filter((entry): entry is OverlayEntry => !!entry);
}

export function overlayPositionsEqual(prev: OverlayEntry[], next: OverlayEntry[]): boolean {
  if (prev.length !== next.length) {
    return false;
  }

  return prev.every((entry, index) => {
    const candidate = next[index];
    return (
      candidate &&
      entry.comment.conversationId === candidate.comment.conversationId &&
      Math.abs(entry.top - candidate.top) < 0.5 &&
      Math.abs(entry.right - candidate.right) < 0.5
    );
  });
}

export function getDiffDisplayBoxSx(containerSx?: Record<string, unknown>) {
  return {
    position: 'relative' as const,
    '& .content-management-item': {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    '& .diffins': {
      backgroundColor: '#e8f5e8',
      color: '#2e7d32',
      fontWeight: 600,
      padding: '1px 2px',
      borderRadius: '2px',
      textDecoration: 'none',
    },
    '& .diffdel': {
      backgroundColor: '#ffebee',
      color: '#d32f2f',
      textDecoration: 'line-through',
      padding: '1px 2px',
      borderRadius: '2px',
    },
    '& .diffmod': {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      fontWeight: 500,
      padding: '1px 2px',
      borderRadius: '2px',
    },
    ...containerSx,
  };
}
